import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { formatThousands } from "./utils";
import { dateAndTime, getDateString } from "../../../utils/dateutils";

const formatExportHours = (value) => {
  if (value == null || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num) ? num : value;
};

const formatExportVolunteers = (volunteers) => {
  if (Array.isArray(volunteers)) return volunteers.filter(Boolean).join(", ");
  return volunteers ?? "";
};

/**
 * Builds and downloads an Excel workbook for the given reporting data.
 */
export async function exportReportingToExcel({
  reportingData,
  patientReportingData = [],
  months,
  quarters,
  mapper,
  serviceHour,
  serviceRequest,
  eleventhHourReport = null,
}) {
  const {
    monthlySummary,
    volunteersReporting,
    mileageSummary,
    special5PercentReport,
    volunteerRetention,
    measuresOfExcellent,
  } = reportingData;

  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .replace("T", "_")
    .slice(0, 19);
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(`PDV_Reporting_${timestamp}`);
  const monthsWithYTD = [...months, "Year-to-Date (YTD)"];

  const addSubHeader = (label) => {
    sheet.addRow([label, ...monthsWithYTD]);
    const row = sheet.lastRow;
    row.font = { bold: true };
    row.alignment = { horizontal: "center", vertical: "middle" };
  };

  const addDataRows = (data, label, serviceHourVal, serviceRequestVal) => {
    addSubHeader(label);
    const keys = Object.keys(mapper).filter((key) =>
      data.some((m) => m[key] !== undefined),
    );
    keys.forEach((key) => {
      let row;
      if (key === "totalServiceHours") {
        row = [`Total ${serviceHourVal} Hours`];
      } else if (key === "totalServiceRequest") {
        row = [`Total ${serviceRequestVal} Request`];
      } else {
        row = [mapper[key]];
      }
      let total = 0;
      months.forEach((_, idx) => {
        const monthNum = idx + 1;
        const item = data.find((m) => m.month === monthNum) || {};
        const val = parseFloat(item[key]) || 0;
        total += val;
        row.push(val ?? formatThousands(val));
      });
      row.push(total ?? formatThousands(total));
      const addedRow = sheet.addRow(row);
      addedRow.alignment = { vertical: "middle", horizontal: "center" };
    });
    sheet.addRow([]);
  };

  if (monthlySummary?.length) addDataRows(monthlySummary, "Monthly Summary");
  if (patientReportingData?.length)
    addDataRows(
      patientReportingData,
      "Patient Reporting",
      serviceHour,
      serviceRequest,
    );
  if (volunteersReporting?.length)
    addDataRows(volunteersReporting, "Volunteers Reporting");
  if (mileageSummary?.length) addDataRows(mileageSummary, "Mileage Summary");

  if (
    special5PercentReport &&
    Object.keys(special5PercentReport).length > 0
  ) {
    const fromDate = getDateString(special5PercentReport?.startDate);
    const toDate = getDateString(special5PercentReport?.endDate);
    const headerRow = sheet.addRow([
      "Special 5% Report",
      `12 Months (From ${fromDate} to ${toDate})`,
    ]);
    headerRow.font = { bold: true };
    headerRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    sheet.columns = [{ width: 40 }, { width: 40 }];
    Object.keys(special5PercentReport).forEach((key) => {
      if (key === "startDate" || key === "endDate") return;
      const val = special5PercentReport[key];
      const addedRow = sheet.addRow([
        mapper[key] || key,
        formatThousands(val),
      ]);
      addedRow.alignment = { vertical: "middle", horizontal: "center" };
    });
    sheet.addRow([]);
  }

  if (volunteerRetention && Object.keys(volunteerRetention).length > 0) {
    sheet.addRow(["Volunteer Retention", "Year"]);
    const row = sheet.lastRow;
    row.font = { bold: true };
    row.alignment = { horizontal: "center", vertical: "middle" };
    sheet.columns = [{ width: 40 }, { width: 20 }];
    Object.keys(volunteerRetention).forEach((key) => {
      const val = volunteerRetention[key];
      const addedRow = sheet.addRow([
        mapper[key] || key,
        formatThousands(val),
      ]);
      addedRow.alignment = { vertical: "middle", horizontal: "center" };
    });
    sheet.addRow([]);
  }

  if (measuresOfExcellent?.length) {
    sheet.addRow(["Measure Of Excellence", ...quarters]);
    const row = sheet.lastRow;
    row.font = { bold: true };
    row.alignment = { horizontal: "center", vertical: "middle" };
    const keys = Object.keys(mapper).filter((key) =>
      measuresOfExcellent?.some((m) => m[key] !== undefined),
    );
    keys.forEach((key) => {
      const rowData = [mapper[key]];
      quarters.forEach((_, idx) => {
        const quarterNum = idx + 1;
        const item =
          measuresOfExcellent?.find((m) => m.quarter === quarterNum) || {};
        const val = parseFloat(item[key]) || 0;
        rowData.push(val ?? formatThousands(val));
      });
      const addedRow = sheet.addRow(rowData);
      addedRow.alignment = { vertical: "middle", horizontal: "center" };
    });
    sheet.addRow([]);
  }

  if (eleventhHourReport) {
    const eleventhSheet = workbook.addWorksheet("11th Hour Reporting");
    const fromDate = eleventhHourReport.startDate
      ? getDateString(eleventhHourReport.startDate)
      : "";
    const toDate = eleventhHourReport.endDate
      ? getDateString(eleventhHourReport.endDate)
      : "";
    const teamNames = Array.isArray(eleventhHourReport.teams)
      ? eleventhHourReport.teams.filter(Boolean).join(", ")
      : (eleventhHourReport.teams ?? "");

    const titleRow = eleventhSheet.addRow(["11th Hour Reporting"]);
    titleRow.font = { bold: true };
    titleRow.alignment = { horizontal: "left", vertical: "middle" };

    const dateRangeRow = eleventhSheet.addRow([
      "Date Range",
      fromDate && toDate ? `From ${fromDate} to ${toDate}` : "",
    ]);
    dateRangeRow.font = { bold: true };
    dateRangeRow.alignment = { horizontal: "left", vertical: "middle" };

    const teamsRow = eleventhSheet.addRow(["Teams", teamNames]);
    teamsRow.font = { bold: true };
    teamsRow.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };

    eleventhSheet.addRow([]);

    const eleventhHourColumns = [
      { header: "Patient Name", value: (row) => row?.patientName ?? "" },
      { header: "Team", value: (row) => row?.team ?? "" },
      { header: "Coordinator", value: (row) => row?.coordinator ?? "" },
      {
        header: "Requested Date",
        value: (row) =>
          row?.requestedDate ? getDateString(row.requestedDate) : "",
      },
      {
        header: "Requested Start",
        value: (row) =>
          row?.requestedStartTime ? dateAndTime(row.requestedStartTime) : "",
      },
      {
        header: "Requested End",
        value: (row) =>
          row?.requestedEndTime ? dateAndTime(row.requestedEndTime) : "",
      },
      {
        header: "Total Hours Requested",
        value: (row) => formatExportHours(row?.totalHoursRequested),
      },
      {
        header: "Hours Utilized",
        value: (row) => formatExportHours(row?.hoursUtilized),
      },
      {
        header: "Volunteers",
        value: (row) => formatExportVolunteers(row?.volunteers),
      },
    ];

    const headerRow = eleventhSheet.addRow(
      eleventhHourColumns.map((column) => column.header),
    );
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    (eleventhHourReport.rows ?? []).forEach((row) => {
      const addedRow = eleventhSheet.addRow(
        eleventhHourColumns.map((column) => column.value(row)),
      );
      addedRow.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    });

    [28, 24, 24, 20, 24, 24, 26, 18, 40].forEach((width, index) => {
      eleventhSheet.getColumn(index + 1).width = width;
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `PDV_Reporting_${timestamp}.xlsx`);
}
